import errorCodes from "../generated/errorcodes.json";
import messageCodes from "../generated/messagecodes.json";
import { APP_NAME } from "../config/app";

export type TranslationCatalog = {
  ui: Record<string, string>;
  errors: Record<string, string>;
  messages: Record<string, string>;
};

const enErrors: Record<string, string> = {
  INTERNAL_ERROR: "Something went wrong. Please try again.",
  VALIDATION_FAILED: "Please check the highlighted fields.",
  RESOURCE_NOT_FOUND: "We could not find that item.",
  AUTH_UNAUTHORIZED: "Please sign in again.",
  AUTH_FORBIDDEN: "You do not have permission to do that.",
  AUTH_INVALID_CREDENTIALS: "The email or password is incorrect.",
  AUTH_MFA_REQUIRED: "Enter your verification code.",
  AUTH_INVALID_MFA_CODE: "The verification code is incorrect.",
  AUTH_EMAIL_ALREADY_REGISTERED: "An account with this email already exists.",
  AUTH_PASSKEY_CHALLENGE_NOT_FOUND: "The passkey challenge expired. Try again.",
  AUTH_OIDC_UNAVAILABLE: "This sign-in provider is not available.",
  AUTH_OIDC_INVALID_STATE: "The sign-in request expired. Please try again.",
  AUTH_OIDC_TOKEN_EXCHANGE_FAILED: "The sign-in provider could not complete the login.",
  AUTH_OIDC_UNVERIFIED_EMAIL: "The sign-in provider did not return a verified email address.",
  AUTH_OIDC_ACCOUNT_LINK_CONFLICT: "This provider account is already linked to another account.",
  AUTH_OAUTH_UNSUPPORTED_GRANT: "This OAuth flow is not supported.",
  AUTH_INVALID_RESET_TOKEN: "The reset link is invalid or expired.",
  AUTH_RESET_TOKEN_EXPIRED: "The reset link has expired.",
  AUTH_RESET_TOKEN_USED: "This reset link has already been used.",
  AUTH_SIGNUP_DISABLED: "Public account creation is currently disabled.",
  AUTH_CSRF_REQUIRED: "Security verification failed. Refresh and try again.",
  AUTH_CURRENT_PASSWORD_REQUIRED: "Enter your current password.",
  AUTH_TOKEN_INVALID: "The token is invalid or expired.",
  INVITATION_INVALID: "The invitation link is invalid.",
  INVITATION_EXPIRED: "The invitation link has expired.",
  INVITATION_USED: "This invitation has already been used.",
  ADMIN_SELF_DISABLE_FORBIDDEN: "You cannot disable your own admin user.",
  IDEMPOTENCY_REPLAY_MISMATCH: "This retry does not match the original request.",
  RATE_LIMITED: "Too many attempts. Please wait and try again.",
  TENANT_NOT_FOUND: "The workspace was not found.",
  USER_NOT_FOUND: "The user was not found.",
  CUP_NOT_FOUND: "The cup was not found.",
  DRINK_ENTRY_NOT_FOUND: "The drink entry was not found.",
  WEIGHT_ENTRY_NOT_FOUND: "The weight entry was not found.",
  REPORT_NOT_FOUND: "The report was not found.",
  FEATURE_GATE_DENIED: "This feature is not enabled for your account.",
  DB_VERIFY_FAILED: "The backend could not verify the database."
};

const deErrors: Record<string, string> = {
  INTERNAL_ERROR: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
  VALIDATION_FAILED: "Bitte prüfe die markierten Felder.",
  RESOURCE_NOT_FOUND: "Dieser Eintrag wurde nicht gefunden.",
  AUTH_UNAUTHORIZED: "Bitte melde dich erneut an.",
  AUTH_FORBIDDEN: "Du hast dafür keine Berechtigung.",
  AUTH_INVALID_CREDENTIALS: "E-Mail oder Passwort ist falsch.",
  AUTH_MFA_REQUIRED: "Gib deinen Bestätigungscode ein.",
  AUTH_INVALID_MFA_CODE: "Der Bestätigungscode ist falsch.",
  AUTH_EMAIL_ALREADY_REGISTERED: "Für diese E-Mail gibt es bereits ein Konto.",
  AUTH_PASSKEY_CHALLENGE_NOT_FOUND: "Die Passkey-Anfrage ist abgelaufen. Versuche es erneut.",
  AUTH_OIDC_UNAVAILABLE: "Dieser Anmeldeanbieter ist nicht verfügbar.",
  AUTH_OIDC_INVALID_STATE: "Die Anmeldung ist abgelaufen. Bitte versuche es erneut.",
  AUTH_OIDC_TOKEN_EXCHANGE_FAILED: "Der Anmeldeanbieter konnte die Anmeldung nicht abschließen.",
  AUTH_OIDC_UNVERIFIED_EMAIL: "Der Anmeldeanbieter hat keine bestätigte E-Mail-Adresse zurückgegeben.",
  AUTH_OIDC_ACCOUNT_LINK_CONFLICT: "Dieses Anbieter-Konto ist bereits mit einem anderen Konto verknüpft.",
  AUTH_OAUTH_UNSUPPORTED_GRANT: "Dieser OAuth-Ablauf wird nicht unterstützt.",
  AUTH_INVALID_RESET_TOKEN: "Der Link ist ungültig oder abgelaufen.",
  AUTH_RESET_TOKEN_EXPIRED: "Der Link ist abgelaufen.",
  AUTH_RESET_TOKEN_USED: "Dieser Link wurde bereits verwendet.",
  AUTH_SIGNUP_DISABLED: "Die öffentliche Kontoerstellung ist aktuell deaktiviert.",
  AUTH_CSRF_REQUIRED: "Die Sicherheitsprüfung ist fehlgeschlagen. Lade neu und versuche es erneut.",
  AUTH_CURRENT_PASSWORD_REQUIRED: "Gib dein aktuelles Passwort ein.",
  AUTH_TOKEN_INVALID: "Das Token ist ungültig oder abgelaufen.",
  INVITATION_INVALID: "Der Einladungslink ist ungültig.",
  INVITATION_EXPIRED: "Der Einladungslink ist abgelaufen.",
  INVITATION_USED: "Diese Einladung wurde bereits verwendet.",
  ADMIN_SELF_DISABLE_FORBIDDEN: "Du kannst deinen eigenen Admin-Benutzer nicht deaktivieren.",
  IDEMPOTENCY_REPLAY_MISMATCH: "Diese Wiederholung passt nicht zur ursprünglichen Anfrage.",
  RATE_LIMITED: "Zu viele Versuche. Bitte warte kurz.",
  TENANT_NOT_FOUND: "Der Arbeitsbereich wurde nicht gefunden.",
  USER_NOT_FOUND: "Der Benutzer wurde nicht gefunden.",
  CUP_NOT_FOUND: "Der Becher wurde nicht gefunden.",
  DRINK_ENTRY_NOT_FOUND: "Der Trinkeintrag wurde nicht gefunden.",
  WEIGHT_ENTRY_NOT_FOUND: "Der Gewichtseintrag wurde nicht gefunden.",
  REPORT_NOT_FOUND: "Der Bericht wurde nicht gefunden.",
  FEATURE_GATE_DENIED: "Diese Funktion ist für dein Konto nicht aktiviert.",
  DB_VERIFY_FAILED: "Das Backend konnte die Datenbank nicht prüfen."
};

const enMessages: Record<string, string> = {
  EMAIL_VERIFY_SUBJECT: "Verify your email",
  PASSWORD_RESET_SUBJECT: "Reset your password",
  REPORT_READY_SUBJECT: "Your report is ready",
  REMINDER_SUBJECT: "Time for water",
  EXPORT_READY_SUBJECT: "Your export is ready"
};

const deMessages: Record<string, string> = {
  EMAIL_VERIFY_SUBJECT: "E-Mail bestätigen",
  PASSWORD_RESET_SUBJECT: "Passwort zurücksetzen",
  REPORT_READY_SUBJECT: "Dein Bericht ist bereit",
  REMINDER_SUBJECT: "Zeit für Wasser",
  EXPORT_READY_SUBJECT: "Dein Export ist bereit"
};

const uiEn: Record<string, string> = {
  "app.name": APP_NAME,
  "common.loading": "Loading",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.close": "Close",
  "common.add": "Add",
  "common.copy": "Copy",
  "common.create": "Create",
  "common.update": "Update",
  "common.refresh": "Refresh",
  "common.retry": "Retry",
  "common.done": "Done",
  "common.enabled": "Enabled",
  "common.disabled": "Disabled",
  "common.unknown": "Unknown",
  "common.empty": "Nothing here yet.",
  "common.permissionDenied": "Permission denied",
  "common.requestId": "Request ID",
  "common.ml": "ml",
  "auth.login": "Log in",
  "auth.register": "Create account",
  "auth.logout": "Log out",
  "auth.logoutAll": "Log out all devices",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.displayName": "Name",
  "auth.currentPassword": "Current password",
  "auth.newPassword": "New password",
  "auth.repeatNewPassword": "Repeat new password",
  "auth.totpCode": "Verification code",
  "auth.mfaTitle": "Verification code",
  "auth.mfaPrompt": "Enter the code from your authenticator app.",
  "auth.verifyContinue": "Verify and continue",
  "auth.useDifferentLogin": "Use different login",
  "auth.forgotPassword": "Reset password",
  "auth.resetSent": "If the account exists, a reset email was sent.",
  "auth.resetSuccess": "Password updated. Please sign in.",
  "auth.resetConfirm": "Set new password",
  "auth.passwordsMismatch": "The passwords do not match.",
  "auth.loginFailed": "Sign-in failed.",
  "auth.fieldInvalid": "Please check this field.",
  "auth.sessionMissing": "Signed in, but the session could not be loaded.",
  "auth.registerSuccessLogin": "Account created. Please sign in.",
  "auth.signupDisabled": "Public account creation is disabled. Ask an admin for an invitation.",
  "auth.google": "Continue with Google",
  "auth.apple": "Continue with Apple",
  "auth.passkeyLogin": "Sign in with passkey",
  "auth.passkeyUnsupported": "Passkeys are not available in this browser.",
  "auth.passkeyCancelled": "Passkey sign-in was cancelled.",
  "auth.passkeysExperimental": "Passkeys are available when enabled for your account.",
  "auth.invitationTitle": "Set your password",
  "auth.invitationFor": "Invitation for {email}",
  "auth.invitationSubmit": "Save password",
  "auth.invitationComplete": "Password saved. Please sign in.",
  "nav.today": "Today",
  "nav.analytics": "Analytics",
  "nav.reports": "Reports",
  "nav.settings": "Settings",
  "nav.admin": "Admin",
  "onboarding.title": "Set up your daily target",
  "onboarding.welcome": "A quick setup keeps tracking accurate.",
  "onboarding.height": "Height",
  "onboarding.birthYear": "Birth year",
  "onboarding.reference": "Hydration reference",
  "onboarding.referenceHelp": "Which reference value should we use?",
  "onboarding.female": "Female",
  "onboarding.male": "Male",
  "onboarding.units": "Units",
  "onboarding.volumeUnit": "Volume",
  "onboarding.lengthUnit": "Length",
  "onboarding.weightUnit": "Weight",
  "onboarding.timezone": "Timezone",
  "onboarding.autoTimezone": "Auto",
  "onboarding.fixedTimezone": "Fixed",
  "onboarding.fixedTimezoneLabel": "Fixed timezone",
  "onboarding.preview": "Recommendation",
  "onboarding.estimate": "Estimated goal",
  "onboarding.customGoal": "Custom goal",
  "onboarding.customGoalOptional": "Custom goal, optional ({unit})",
  "onboarding.weightOptional": "Optional weight",
  "onboarding.previewPending": "-",
  "onboarding.completeRequired": "Add height, birth year, and hydration reference.",
  "onboarding.refreshIncomplete": "Setup was saved, but your profile still looks incomplete. Try again in a moment.",
  "onboarding.saveContinue": "Save and continue",
  "onboarding.estimateNote": "This is an estimate, not medical advice.",
  "today.title": "Today",
  "today.yesterday": "Yesterday",
  "today.previousDay": "Previous day",
  "today.nextDay": "Next day",
  "today.selectDate": "Select date",
  "today.progress": "Daily progress",
  "today.cups": "Cups",
  "today.addCup": "Add cup",
  "today.editCup": "Edit cup",
  "today.deleteCup": "Delete cup",
  "today.cupName": "Cup name",
  "today.cupVolume": "Volume",
  "today.cupIcon": "Icon",
  "today.entries": "Entries",
  "today.noCups": "Add your first cup to log water in one tap.",
  "today.noEntries": "No drinks logged today.",
  "today.added": "Added {amount}",
  "today.undo": "Undo",
  "today.undone": "Entry removed.",
  "today.entryDeleted": "Entry deleted.",
  "today.restore": "Restore",
  "today.editEntry": "Edit {amount}",
  "today.weight": "Weight",
  "today.addWeight": "Add weight",
  "today.weightLastEntered": "Entered {date}",
  "today.time": "Time",
  "today.note": "Note",
  "today.streak": "Streak",
  "analytics.title": "Analytics",
  "analytics.trend": "Hydration trend",
  "analytics.trendHint": "Daily totals and averaged longer ranges.",
  "analytics.range": "Range",
  "analytics.oneWeek": "1 week",
  "analytics.oneMonth": "1 month",
  "analytics.sixMonths": "6 months",
  "analytics.oneYear": "1 year",
  "analytics.allTime": "Since start",
  "analytics.custom": "Custom",
  "analytics.start": "Start",
  "analytics.end": "End",
  "analytics.average": "Average",
  "analytics.noTrendData": "No trend data yet.",
  "analytics.bucket.day": "Daily",
  "analytics.bucket.week": "Weekly average",
  "analytics.bucket.month": "Monthly average",
  "reports.title": "Reports",
  "reports.create": "Create report",
  "reports.kind": "Kind",
  "reports.format": "Format",
  "reports.dateRange": "Date range",
  "reports.from": "From",
  "reports.to": "To",
  "reports.backendDefaultRange": "This service currently uses its configured report range.",
  "reports.status": "Status",
  "reports.download": "Download",
  "reports.emailUnavailable": "Email delivery is not available for reports on this service.",
  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.profile": "Profile",
  "settings.units": "Units",
  "settings.language": "Language",
  "settings.theme": "Theme",
  "settings.themeSystem": "System",
  "settings.themeLight": "Light",
  "settings.themeDark": "Dark",
  "settings.goal": "Hydration goal",
  "settings.notifications": "Notifications",
  "settings.streaks": "Streaks",
  "settings.showStreaks": "Show streaks",
  "settings.security": "Security",
  "settings.sessions": "Sessions",
  "settings.passkeys": "Passkeys",
  "settings.recoveryCodes": "Recovery codes",
  "settings.totp": "Authenticator app",
  "settings.dataExport": "Data export",
  "settings.accountDeletion": "Account deletion",
  "settings.accountDeletionUnavailable": "Contact support to request account deletion.",
  "settings.accountDeletionDetail": "Delete your account and revoke all sessions.",
  "settings.accountDeletionConfirm": "Delete your account? This cannot be undone.",
  "settings.deleteAccount": "Delete account",
  "settings.emailNotifications": "Email",
  "settings.webPush": "Web push",
  "settings.reminders": "Reminders",
  "settings.sendTest": "Send test",
  "settings.publicKeyMissing": "Browser push must be enabled by the service operator.",
  "settings.generateCodes": "Generate codes",
  "settings.setupTotp": "Set up authenticator",
  "settings.verifyTotp": "Verify authenticator",
  "settings.totpScan": "Scan this QR code with your authenticator app.",
  "settings.totpQr": "Authenticator setup QR code",
  "settings.totpManual": "Or enter this code manually:",
  "settings.addPasskey": "Add passkey",
  "settings.passkey": "Passkey",
  "settings.currentSession": "Current",
  "settings.created": "Created",
  "settings.lastUsed": "Last used",
  "settings.neverUsed": "Never used",
  "settings.lastActive": "Last active",
  "settings.expires": "Expires",
  "settings.ip": "IP",
  "settings.ipUnavailable": "Unavailable",
  "settings.location": "Location",
  "settings.revokeSession": "Log out",
  "settings.revokeCurrentSession": "Log out this device",
  "settings.sessionRevoked": "Revoked",
  "settings.hydrationDetails": "Hydration details",
  "settings.estimatedGoal": "Estimated goal",
  "admin.title": "Admin",
  "admin.users": "Users",
  "admin.audit": "Audit",
  "admin.reports": "Reports",
  "admin.features": "Features",
  "admin.jobs": "Jobs",
  "admin.enableUser": "Enable",
  "admin.disableUser": "Disable",
  "admin.deleteUser": "Delete user",
  "admin.deleteUserConfirm": "Delete {email}? This cannot be undone.",
  "admin.cannotDisableSelf": "You cannot disable your own admin user.",
  "admin.cannotChangeOwnRole": "You cannot change your own role.",
  "admin.changeRole": "Change role",
  "admin.self": "You",
  "admin.enqueue": "Enqueue",
  "admin.role": "Role",
  "admin.status": "Status",
  "admin.action": "Action",
  "admin.resource": "Resource",
  "admin.actor": "Actor",
  "admin.featureCode": "Feature",
  "admin.jobType": "Job type",
  "admin.inviteUser": "Invite user",
  "admin.inviteEmail": "Email to invite",
  "admin.inviteSent": "Invitation email sent.",
  "admin.inviteSentWithExpiry": "Invitation email sent. Link expires {date}.",
  "admin.roleMember": "Member",
  "admin.roleAdmin": "Admin"
};

const uiDe: Record<string, string> = {
  "app.name": APP_NAME,
  "common.loading": "Lädt",
  "common.save": "Speichern",
  "common.cancel": "Abbrechen",
  "common.delete": "Löschen",
  "common.edit": "Bearbeiten",
  "common.close": "Schließen",
  "common.add": "Hinzufügen",
  "common.copy": "Kopieren",
  "common.create": "Erstellen",
  "common.update": "Aktualisieren",
  "common.refresh": "Aktualisieren",
  "common.retry": "Erneut versuchen",
  "common.done": "Fertig",
  "common.enabled": "Aktiv",
  "common.disabled": "Inaktiv",
  "common.unknown": "Unbekannt",
  "common.empty": "Noch nichts vorhanden.",
  "common.permissionDenied": "Keine Berechtigung",
  "common.requestId": "Request-ID",
  "common.ml": "ml",
  "auth.login": "Anmelden",
  "auth.register": "Konto erstellen",
  "auth.logout": "Abmelden",
  "auth.logoutAll": "Alle Geräte abmelden",
  "auth.email": "E-Mail",
  "auth.password": "Passwort",
  "auth.displayName": "Name",
  "auth.currentPassword": "Aktuelles Passwort",
  "auth.newPassword": "Neues Passwort",
  "auth.repeatNewPassword": "Neues Passwort wiederholen",
  "auth.totpCode": "Bestätigungscode",
  "auth.mfaTitle": "Bestätigungscode",
  "auth.mfaPrompt": "Gib den Code aus deiner Authenticator-App ein.",
  "auth.verifyContinue": "Bestätigen und weiter",
  "auth.useDifferentLogin": "Andere Anmeldung verwenden",
  "auth.forgotPassword": "Passwort zurücksetzen",
  "auth.resetSent": "Falls das Konto existiert, wurde eine E-Mail gesendet.",
  "auth.resetSuccess": "Passwort aktualisiert. Bitte melde dich an.",
  "auth.resetConfirm": "Neues Passwort setzen",
  "auth.passwordsMismatch": "Die Passwörter stimmen nicht überein.",
  "auth.loginFailed": "Anmeldung fehlgeschlagen.",
  "auth.fieldInvalid": "Bitte prüfe dieses Feld.",
  "auth.sessionMissing": "Angemeldet, aber die Sitzung konnte nicht geladen werden.",
  "auth.registerSuccessLogin": "Konto erstellt. Bitte melde dich an.",
  "auth.signupDisabled": "Die öffentliche Kontoerstellung ist deaktiviert. Bitte einen Admin um eine Einladung.",
  "auth.google": "Mit Google fortfahren",
  "auth.apple": "Mit Apple fortfahren",
  "auth.passkeyLogin": "Mit Passkey anmelden",
  "auth.passkeyUnsupported": "Passkeys sind in diesem Browser nicht verfügbar.",
  "auth.passkeyCancelled": "Passkey-Anmeldung wurde abgebrochen.",
  "auth.passkeysExperimental": "Passkeys sind verfügbar, wenn sie für dein Konto aktiviert sind.",
  "auth.invitationTitle": "Passwort festlegen",
  "auth.invitationFor": "Einladung für {email}",
  "auth.invitationSubmit": "Passwort speichern",
  "auth.invitationComplete": "Passwort gespeichert. Bitte melde dich an.",
  "nav.today": "Heute",
  "nav.analytics": "Analyse",
  "nav.reports": "Berichte",
  "nav.settings": "Einstellungen",
  "nav.admin": "Admin",
  "onboarding.title": "Tagesziel einrichten",
  "onboarding.welcome": "Eine kurze Einrichtung macht das Tracking genauer.",
  "onboarding.height": "Größe",
  "onboarding.birthYear": "Geburtsjahr",
  "onboarding.reference": "Hydration-Referenz",
  "onboarding.referenceHelp": "Welchen Referenzwert sollen wir nutzen?",
  "onboarding.female": "Weiblich",
  "onboarding.male": "Männlich",
  "onboarding.units": "Einheiten",
  "onboarding.volumeUnit": "Volumen",
  "onboarding.lengthUnit": "Länge",
  "onboarding.weightUnit": "Gewicht",
  "onboarding.timezone": "Zeitzone",
  "onboarding.autoTimezone": "Auto",
  "onboarding.fixedTimezone": "Fest",
  "onboarding.fixedTimezoneLabel": "Feste Zeitzone",
  "onboarding.preview": "Empfehlung",
  "onboarding.estimate": "Geschätztes Ziel",
  "onboarding.customGoal": "Eigenes Ziel",
  "onboarding.customGoalOptional": "Eigenes Ziel, optional ({unit})",
  "onboarding.weightOptional": "Gewicht optional",
  "onboarding.previewPending": "-",
  "onboarding.completeRequired": "Trage Größe, Geburtsjahr und Hydration-Referenz ein.",
  "onboarding.refreshIncomplete": "Die Einrichtung wurde gespeichert, aber dein Profil wirkt noch unvollständig. Versuche es gleich erneut.",
  "onboarding.saveContinue": "Speichern und weiter",
  "onboarding.estimateNote": "Dies ist eine Schätzung, kein medizinischer Rat.",
  "today.title": "Heute",
  "today.yesterday": "Gestern",
  "today.previousDay": "Vorheriger Tag",
  "today.nextDay": "Nächster Tag",
  "today.selectDate": "Datum wählen",
  "today.progress": "Tagesfortschritt",
  "today.cups": "Becher",
  "today.addCup": "Becher hinzufügen",
  "today.editCup": "Becher bearbeiten",
  "today.deleteCup": "Becher löschen",
  "today.cupName": "Name",
  "today.cupVolume": "Volumen",
  "today.cupIcon": "Symbol",
  "today.entries": "Einträge",
  "today.noCups": "Füge deinen ersten Becher hinzu, um mit einem Tipp zu loggen.",
  "today.noEntries": "Heute noch keine Getränke.",
  "today.added": "{amount} hinzugefügt",
  "today.undo": "Rückgängig",
  "today.undone": "Eintrag entfernt.",
  "today.entryDeleted": "Eintrag gelöscht.",
  "today.restore": "Wiederherstellen",
  "today.editEntry": "{amount} bearbeiten",
  "today.weight": "Gewicht",
  "today.addWeight": "Gewicht hinzufügen",
  "today.weightLastEntered": "Eingetragen am {date}",
  "today.time": "Uhrzeit",
  "today.note": "Notiz",
  "today.streak": "Serie",
  "analytics.title": "Analyse",
  "analytics.trend": "Trinkverlauf",
  "analytics.trendHint": "Tageswerte und gemittelte lange Zeiträume.",
  "analytics.range": "Zeitraum",
  "analytics.oneWeek": "1 Woche",
  "analytics.oneMonth": "1 Monat",
  "analytics.sixMonths": "6 Monate",
  "analytics.oneYear": "1 Jahr",
  "analytics.allTime": "Seit Start",
  "analytics.custom": "Eigener Zeitraum",
  "analytics.start": "Start",
  "analytics.end": "Ende",
  "analytics.average": "Durchschnitt",
  "analytics.noTrendData": "Noch keine Verlaufsdaten.",
  "analytics.bucket.day": "Täglich",
  "analytics.bucket.week": "Wochendurchschnitt",
  "analytics.bucket.month": "Monatsdurchschnitt",
  "reports.title": "Berichte",
  "reports.create": "Bericht erstellen",
  "reports.kind": "Art",
  "reports.format": "Format",
  "reports.dateRange": "Zeitraum",
  "reports.from": "Von",
  "reports.to": "Bis",
  "reports.backendDefaultRange": "Dieser Dienst nutzt aktuell seinen konfigurierten Berichtszeitraum.",
  "reports.status": "Status",
  "reports.download": "Herunterladen",
  "reports.emailUnavailable": "E-Mail-Zustellung ist für Berichte in diesem Dienst nicht verfügbar.",
  "settings.title": "Einstellungen",
  "settings.account": "Konto",
  "settings.profile": "Profil",
  "settings.units": "Einheiten",
  "settings.language": "Sprache",
  "settings.theme": "Darstellung",
  "settings.themeSystem": "System",
  "settings.themeLight": "Hell",
  "settings.themeDark": "Dunkel",
  "settings.goal": "Trinkziel",
  "settings.notifications": "Benachrichtigungen",
  "settings.streaks": "Serien",
  "settings.showStreaks": "Serien anzeigen",
  "settings.security": "Sicherheit",
  "settings.sessions": "Sitzungen",
  "settings.passkeys": "Passkeys",
  "settings.recoveryCodes": "Wiederherstellungscodes",
  "settings.totp": "Authenticator-App",
  "settings.dataExport": "Datenexport",
  "settings.accountDeletion": "Konto löschen",
  "settings.accountDeletionUnavailable": "Kontaktiere den Support, um die Kontolöschung anzufragen.",
  "settings.accountDeletionDetail": "Lösche dein Konto und melde alle Sitzungen ab.",
  "settings.accountDeletionConfirm": "Konto löschen? Das kann nicht rückgängig gemacht werden.",
  "settings.deleteAccount": "Konto löschen",
  "settings.emailNotifications": "E-Mail",
  "settings.webPush": "Web Push",
  "settings.reminders": "Erinnerungen",
  "settings.sendTest": "Test senden",
  "settings.publicKeyMissing": "Browser-Push muss vom Betreiber aktiviert werden.",
  "settings.generateCodes": "Codes erzeugen",
  "settings.setupTotp": "Authenticator einrichten",
  "settings.verifyTotp": "Authenticator bestätigen",
  "settings.totpScan": "Scanne diesen QR-Code mit deiner Authenticator-App.",
  "settings.totpQr": "QR-Code für Authenticator-Einrichtung",
  "settings.totpManual": "Oder gib diesen Code manuell ein:",
  "settings.addPasskey": "Passkey hinzufügen",
  "settings.passkey": "Passkey",
  "settings.currentSession": "Aktuell",
  "settings.created": "Erstellt",
  "settings.lastUsed": "Zuletzt genutzt",
  "settings.neverUsed": "Noch nie genutzt",
  "settings.lastActive": "Zuletzt aktiv",
  "settings.expires": "Läuft ab",
  "settings.ip": "IP",
  "settings.ipUnavailable": "Nicht verfügbar",
  "settings.location": "Ort",
  "settings.revokeSession": "Abmelden",
  "settings.revokeCurrentSession": "Dieses Gerät abmelden",
  "settings.sessionRevoked": "Abgemeldet",
  "settings.hydrationDetails": "Trinkdetails",
  "settings.estimatedGoal": "Geschätztes Ziel",
  "admin.title": "Admin",
  "admin.users": "Benutzer",
  "admin.audit": "Audit",
  "admin.reports": "Berichte",
  "admin.features": "Features",
  "admin.jobs": "Jobs",
  "admin.enableUser": "Aktivieren",
  "admin.disableUser": "Deaktivieren",
  "admin.deleteUser": "Benutzer löschen",
  "admin.deleteUserConfirm": "{email} löschen? Das kann nicht rückgängig gemacht werden.",
  "admin.cannotDisableSelf": "Du kannst deinen eigenen Admin-Benutzer nicht deaktivieren.",
  "admin.cannotChangeOwnRole": "Du kannst deine eigene Rolle nicht ändern.",
  "admin.changeRole": "Rolle ändern",
  "admin.self": "Du",
  "admin.enqueue": "Einreihen",
  "admin.role": "Rolle",
  "admin.status": "Status",
  "admin.action": "Aktion",
  "admin.resource": "Ressource",
  "admin.actor": "Akteur",
  "admin.featureCode": "Feature",
  "admin.jobType": "Job-Typ",
  "admin.inviteUser": "Benutzer einladen",
  "admin.inviteEmail": "E-Mail für Einladung",
  "admin.inviteSent": "Einladungs-E-Mail wurde gesendet.",
  "admin.inviteSentWithExpiry": "Einladungs-E-Mail wurde gesendet. Link läuft am {date} ab.",
  "admin.roleMember": "Mitglied",
  "admin.roleAdmin": "Admin"
};

export const translations = {
  en: {
    ui: uiEn,
    errors: completeRegistry(enErrors, errorCodes, "Something went wrong. Please try again."),
    messages: completeRegistry(enMessages, messageCodes, "Message")
  },
  de: {
    ui: uiDe,
    errors: completeRegistry(deErrors, errorCodes, "Etwas ist schiefgelaufen. Bitte versuche es erneut."),
    messages: completeRegistry(deMessages, messageCodes, "Nachricht")
  }
} satisfies Record<"en" | "de", TranslationCatalog>;

function completeRegistry(source: Record<string, string>, codes: string[], fallback: string): Record<string, string> {
  return Object.fromEntries(codes.map((code) => [code, source[code] ?? fallback]));
}
